import createFilterOptions from 'react-select-fast-filter-options'

import type { DropdownOption } from 'types/ui'

import {
  artistSortTypesWithReverse,
  extendedSortTypesWithReverse,
} from '@disco/constants/SortTypes'

// import type { SearchFilters } from '@disco/state-management/reducers/search'
type SearchFilters = any

export type SearchFilterValue = string | number | Array<string | number>

export type SearchFilter = {
  key: string
  value?: SearchFilterValue
  start?: string | number
  end?: string | number
  type?: string
}

/**
 * Creates an indexed search function for dropdown options with custom tokenization.
 *
 * Builds a fast search index for dropdown options using react-select-fast-filter-options
 * with custom tokenization that includes ampersands and handles multiple languages.
 *
 * @param options - Array of dropdown options to index
 * @returns Indexed search function for filtering options
 *
 * @example
 * ```typescript
 * const options = [
 *   { label: 'Rock & Roll', value: 'rock' },
 *   { label: 'Jazz Music', value: 'jazz' },
 *   { label: 'Hip-Hop', value: 'hiphop' }
 * ]
 *
 * const searchFunction = indexDropdownOptions(options)
 * const filtered = searchFunction({ inputValue: 'rock' })
 * // Returns options matching 'rock'
 * ```
 */
export const indexDropdownOptions = (options: DropdownOption[]): any => {
  return createFilterOptions({
    options,
    // Custom tokenizer matches react-select-fast-filter-options default, but adds ampersand to regex: https://github.com/bvaughn/js-search/blob/master/source/Tokenizer/SimpleTokenizer.js
    tokenizer: {
      tokenize: text => text.split(/[^a-zа-яё0-9\-'&]+/i).filter(text => text),
    },
  })
}

/**
 * Filters search filters to include only those with valid values.
 *
 * Removes filters that don't have meaningful values, such as empty keys,
 * missing values for 'equals' type filters, or completely empty filters.
 *
 * @param filters - Array of search filters to validate
 * @returns Array of filters with valid values
 *
 * @example
 * ```typescript
 * const filters = [
 *   { key: 'title', value: 'song', start: '', end: '', type: 'equals' },
 *   { key: '', value: '', start: '', end: '', type: 'equals' }, // invalid
 *   { key: 'duration', value: '', start: '60', end: '180', type: 'range' }
 * ]
 *
 * const valid = getFiltersWithValidValues(filters)
 * // Returns first and third filters only
 * ```
 */
export const getFiltersWithValidValues = (
  filters: SearchFilter[] | null | undefined,
): SearchFilter[] =>
  filters?.filter(
    filter =>
      filter.key &&
      (filter.start || filter.end || filter.value) &&
      !((filter.type === '' || filter.type === 'equals') && !filter.value),
  ) ?? []

/**
 * Checks if any search filters are currently active/set.
 *
 * Determines whether a search has any meaningful filters applied,
 * including search terms, types, groups, tags, dates, or custom filters.
 *
 * @param search - Search filters object to check
 * @returns True if any filters are set, false if search is empty
 *
 * @example
 * ```typescript
 * const emptySearch = {}
 * const hasFilters1 = checkSetFilters(emptySearch) // false
 *
 * const searchWithTerm = { searchTerm: 'rock music' }
 * const hasFilters2 = checkSetFilters(searchWithTerm) // true
 *
 * const searchWithGroups = { groups: ['group1', 'group2'] }
 * const hasFilters3 = checkSetFilters(searchWithGroups) // true
 * ```
 */
export const checkSetFilters = (search: Partial<SearchFilters>): boolean => {
  const {
    searchTerm,
    type,
    groups,
    tags,
    nativeTags,
    startDate,
    duration,
    filters,
  } = search || {}

  const hasFilters =
    Boolean(searchTerm) ||
    type ||
    Boolean(groups?.length) ||
    Boolean(tags?.length) ||
    Boolean(nativeTags?.length) ||
    Boolean(startDate && startDate.getTime()) ||
    getFiltersWithValidValues(filters).length > 0 ||
    // loose inequality: an absent (undefined) duration is not a set filter
    duration != null

  return Boolean(hasFilters)
}

/**
 * Gets appropriate sort options based on search type.
 *
 * Returns different sort option sets depending on whether searching
 * for artists or other content types.
 *
 * @param searchType - Type of search being performed
 * @returns Array of sort options appropriate for the search type
 *
 * @example
 * ```typescript
 * const artistOptions = getSearchOptions('artist')
 * // Returns artist-specific sort options
 *
 * const generalOptions = getSearchOptions('track')
 * // Returns extended sort options for general content
 * ```
 */
export const getSearchOptions = (
  searchType: string,
): typeof extendedSortTypesWithReverse => {
  if (searchType === 'artist') return artistSortTypesWithReverse
  return extendedSortTypesWithReverse
}
